import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, DollarSign, Percent, TrendingUp, TrendingDown } from 'lucide-react';

const emptyForm = {
  name: '',
  type: 'earning',
  amount: '',
  is_percentage: false,
  percentage_of: 'basic_salary',
  description: '',
};

function TemplateDialog({ open, onClose, onSave, template, isEdit }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (template && isEdit) {
      setForm({
        name: template.name || '',
        type: template.type || 'earning',
        amount: template.amount?.toString() || '',
        is_percentage: template.is_percentage || false,
        percentage_of: template.percentage_of || 'basic_salary',
        description: template.description || '',
      });
    } else {
      setForm(emptyForm);
    }
  }, [template, isEdit, open]);

  const handleSave = async () => {
    if (!form.name || !form.amount) {
      toast.error('Please fill required fields');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        type: form.type,
        amount: parseFloat(form.amount),
        is_percentage: form.is_percentage,
        percentage_of: form.is_percentage ? form.percentage_of : null,
        description: form.description,
      };
      await onSave(payload);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? 'Edit Template' : 'Create Template'}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {isEdit ? 'Update the salary component template.' : 'Create a reusable salary component template.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Name *</Label>
            <Input 
              value={form.name} 
              onChange={e => set('name', e.target.value)} 
              className="rounded-sm" 
              placeholder="e.g., Night Shift Allowance"
              data-testid="template-name-input" 
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Type *</Label>
            <Select value={form.type} onValueChange={v => set('type', v)}>
              <SelectTrigger className="rounded-sm" data-testid="template-type-select">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="earning">
                  <span className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-emerald-600" />
                    Earning
                  </span>
                </SelectItem>
                <SelectItem value="deduction">
                  <span className="flex items-center gap-2">
                    <TrendingDown className="w-4 h-4 text-red-600" />
                    Deduction
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between py-2 border-t border-b border-border">
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Percentage Based</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Calculate as % of salary</p>
            </div>
            <Switch 
              checked={form.is_percentage} 
              onCheckedChange={v => set('is_percentage', v)}
              data-testid="template-percentage-switch"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">
              {form.is_percentage ? 'Percentage *' : 'Amount *'}
            </Label>
            <div className="relative">
              <Input 
                value={form.amount} 
                onChange={e => set('amount', e.target.value)} 
                type="number"
                className="rounded-sm pl-10 font-mono" 
                placeholder={form.is_percentage ? "e.g., 10" : "e.g., 5000"}
                data-testid="template-amount-input" 
              />
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                {form.is_percentage ? <Percent className="w-4 h-4" /> : <DollarSign className="w-4 h-4" />}
              </div>
            </div>
          </div>
          {form.is_percentage && (
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Percentage Of *</Label>
              <Select value={form.percentage_of} onValueChange={v => set('percentage_of', v)}>
                <SelectTrigger className="rounded-sm" data-testid="template-percent-of-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="basic_salary">Basic Salary</SelectItem>
                  <SelectItem value="gross_salary">Gross Salary</SelectItem>
                  <SelectItem value="hra">HRA</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Description</Label>
            <Input 
              value={form.description} 
              onChange={e => set('description', e.target.value)} 
              className="rounded-sm" 
              placeholder="Optional description"
              data-testid="template-description-input" 
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#002FA7] hover:bg-[#002585] rounded-sm" data-testid="template-save-btn">
            {saving ? 'Saving...' : isEdit ? 'Update' : 'Create Template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SalaryTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const fetchTemplates = useCallback(() => {
    setLoading(true);
    api.get('/salary-components/templates')
      .then(res => setTemplates(res.data))
      .catch(() => toast.error('Failed to load templates'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleAdd = async (data) => {
    await api.post('/salary-components/templates', data);
    toast.success('Template created');
    fetchTemplates();
  };

  const handleEdit = async (data) => {
    await api.put(`/salary-components/templates/${editTemplate.id}`, data);
    toast.success('Template updated');
    setEditTemplate(null);
    fetchTemplates();
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/salary-components/templates/${deleteId}`);
      toast.success('Template deleted');
      setDeleteId(null);
      fetchTemplates();
    } catch { 
      toast.error('Delete failed'); 
    }
  };

  const earnings = templates.filter(t => t.type === 'earning');
  const deductions = templates.filter(t => t.type === 'deduction');

  return (
    <div data-testid="salary-templates-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground tracking-tight">Salary Components</h2>
          <p className="text-sm text-muted-foreground font-body mt-1">
            Manage reusable salary component templates
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-[#002FA7] hover:bg-[#002585] rounded-sm" data-testid="add-template-btn">
          <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Add Template
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-card border border-border rounded-sm p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-widest">Total Templates</p>
          <p className="text-2xl font-heading font-bold text-foreground mt-1">{templates.length}</p>
        </div>
        <div className="bg-card border border-border rounded-sm p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-widest">Earnings</p>
          <p className="text-2xl font-heading font-bold text-emerald-600 mt-1">{earnings.length}</p>
        </div>
        <div className="bg-card border border-border rounded-sm p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-widest">Deductions</p>
          <p className="text-2xl font-heading font-bold text-red-600 mt-1">{deductions.length}</p>
        </div>
      </div>

      {/* Templates Table */}
      <div className="border border-border bg-card rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-body text-xs uppercase tracking-widest">Name</TableHead>
              <TableHead className="font-body text-xs uppercase tracking-widest">Type</TableHead>
              <TableHead className="font-body text-xs uppercase tracking-widest">Amount</TableHead>
              <TableHead className="font-body text-xs uppercase tracking-widest">Description</TableHead>
              <TableHead className="font-body text-xs uppercase tracking-widest text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-8">Loading...</TableCell>
              </TableRow>
            ) : templates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                  No templates created yet. Click "Add Template" to create one.
                </TableCell>
              </TableRow>
            ) : templates.map(t => (
              <TableRow key={t.id} data-testid={`template-row-${t.id}`}>
                <TableCell className="font-medium">{t.name}</TableCell>
                <TableCell>
                  <Badge 
                    className={`text-xs rounded-sm ${
                      t.type === 'earning' 
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                        : 'bg-red-50 text-red-700 border-red-200'
                    }`}
                  >
                    {t.type === 'earning' ? (
                      <TrendingUp className="w-3 h-3 mr-1 inline" />
                    ) : (
                      <TrendingDown className="w-3 h-3 mr-1 inline" />
                    )}
                    {t.type}
                  </Badge>
                </TableCell>
                <TableCell className="font-mono">
                  {t.is_percentage ? (
                    <span className="text-muted-foreground">{t.amount}% of {t.percentage_of?.replace('_', ' ')}</span>
                  ) : (
                    <span>₹{t.amount.toLocaleString()}</span>
                  )}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                  {t.description || '-'}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setEditTemplate(t)}
                      data-testid={`edit-template-${t.id}`}
                    >
                      <Pencil className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setDeleteId(t.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-template-${t.id}`}
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <TemplateDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSave={handleAdd} isEdit={false} />
      <TemplateDialog open={!!editTemplate} onClose={() => setEditTemplate(null)} onSave={handleEdit} template={editTemplate} isEdit={true} />

      {/* Delete Confirmation */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="rounded-sm max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Delete Template</DialogTitle>
            <DialogDescription>
              This will remove the template and also remove it from all employees who have it applied.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 rounded-sm" data-testid="confirm-delete-template-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
